import { Link } from "react-router-dom";
import {
  Phone, Video, MessageSquare, CreditCard, Smartphone, Wifi,
  Wallet, Send, Receipt, Briefcase, PhoneCall, MessageCircle, ArrowRight
} from "lucide-react";
import SEO from "@/components/site/SEO";

const services = [
  { id: "international-calling", icon: Phone, title: "International Calling", desc: "Crystal-clear voice quality to 50+ African destinations. Per-second billing with no hidden charges and no connection fees. Save up to 80% versus traditional carriers." },
  { id: "voip-calling", icon: PhoneCall, title: "VoIP Calling", desc: "App-to-phone and app-to-app calls over Wi-Fi or mobile data. Perfect for travellers and the diaspora — call any number, anywhere, at lower rates." },
  { id: "video-calling", icon: Video, title: "Video Calling", desc: "HD video calls optimised for African networks. Adaptive bitrate keeps you connected even on patchy connections — one-to-one or in groups." },
  { id: "messaging", icon: MessageCircle, title: "Instant Messaging", desc: "End-to-end secure chat with photos, voice notes, documents and group conversations. Free between OneDial users — anywhere in the world." },
  { id: "calling-cards", icon: CreditCard, title: "Calling Cards", desc: "Pre-paid calling cards for when you’re away from data. Use any phone, dial our access number, enter your PIN — and you’re connected." },
  { id: "airtime", icon: Smartphone, title: "Airtime Top-Up", desc: "Send airtime instantly to any major African network. Surprise mum with credit, support a friend, or top up your own line abroad — all in seconds." },
  { id: "data-bundles", icon: Wifi, title: "Data Bundles", desc: "Pick a data bundle for any African carrier and recipient. From daily bundles to monthly plans — pay in your currency, delivered instantly." },
  { id: "esim", icon: Smartphone, title: "eSIM Services", desc: "Travel-ready eSIMs activated in minutes. Land in any country with data already working — no SIM swaps, no roaming bill shock." },
  { id: "wallet", icon: Wallet, title: "Digital Wallet", desc: "A secure multi-currency wallet to hold, send and receive money. Linked to your card, bank account and OneDial services in one place." },
  { id: "money-transfer", icon: Send, title: "Money Transfer", desc: "Send money home with transparent FX, low fees and same-day delivery. Track every transfer end-to-end from the app." },
  { id: "bill-payments", icon: Receipt, title: "Bill Payments", desc: "Pay electricity, water, internet, TV subscriptions and school fees back home. One tap, full receipt, total peace of mind." },
  { id: "business", icon: Briefcase, title: "Business Accounts", desc: "Built for SMEs: bulk payouts, payroll, invoicing, cross-border payments and team controls. Scale your African operations with one platform." },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Services"
        description="Explore OneDial Africa services: international calling, VoIP, video, messaging, eSIM, airtime, data, wallet, money transfer, bill payments and business accounts."
        path="/services"
      />

      <section className="bg-brand-ink text-white">
        <div className="container-page py-20 lg:py-28">
          <p className="label-eyebrow text-brand-green">Our services</p>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl max-w-3xl text-balance">
            Everything you need to stay connected to Africa.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/75">
            Twelve powerful services. One secure app. Designed and operated by
            Scitygate Telecom UK Ltd from Manchester, United Kingdom.
          </p>
        </div>
      </section>

      <section className="container-page py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start">
            <p className="label-eyebrow">Jump to</p>
            <nav className="mt-4 space-y-1">
              {services.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`services-nav-${s.id}`}
                  className="block rounded-md px-3 py-2 text-sm font-medium text-brand-ink/80 hover:bg-white hover:text-brand-ink border border-transparent hover:border-brand-line transition-colors"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </aside>

          <div className="lg:col-span-9 space-y-6">
            {services.map((s) => (
              <article
                key={s.id}
                id={s.id}
                data-testid={`service-${s.id}`}
                className="rounded-xl border border-brand-line bg-white p-8 hover:border-brand-ink/20 transition-colors scroll-mt-24"
              >
                <div className="flex items-start gap-5">
                  <div className="h-12 w-12 rounded-md bg-brand-ink text-white flex items-center justify-center shrink-0">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-display font-bold text-2xl text-brand-ink">{s.title}</h2>
                    <p className="mt-3 text-brand-muted leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page pb-24">
        <div className="rounded-2xl bg-brand-ink text-white p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <p className="label-eyebrow text-brand-green">Have questions?</p>
            <h3 className="mt-3 font-display font-black text-3xl">We’re here to help.</h3>
            <p className="mt-2 text-white/75 max-w-lg">
              Our UK-based team supports customers and partners across the African diaspora.
            </p>
          </div>
          <Link
            to="/contact-us"
            data-testid="services-cta-contact"
            className="inline-flex items-center gap-2 rounded-md bg-brand-green px-5 py-3 text-sm font-semibold text-white hover:bg-brand-greenHover transition-colors"
          >
            Contact our team <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
